import { useAudienceSession } from '@/contexts/audience-session-context';
import { TOCItem } from '@/types/modules';
import { cn } from '@/lib/utils';
import { emitModuleInteraction } from '@/lib/module-interaction';
import { stripInlineMarkdown } from '@/utils/inline-markdown';
import {
    MODULE_ITEM_ATTR,
    MODULE_ITEM_PARENT_ATTR,
    type ModuleSlice,
} from '@/utils/moduleSplitContract';
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import React, { useMemo } from 'react';

interface TOCRow {
    key: string;
    prefix: string;
    depth: number;
    parent: number;
    item: TOCItem;
    hasChildren: boolean;
}

function flattenTOC(data: Record<string, TOCItem>): TOCRow[] {
    const rows: TOCRow[] = [];
    const walk = (
        entries: Record<string, TOCItem>,
        parentPrefix: string,
        parentKey: string,
        depth: number,
        parent: number,
    ) => {
        Object.entries(entries).forEach(([key, item], i) => {
            const prefix = parentPrefix ? `${parentPrefix}.${i + 1}` : `${i + 1}`;
            const rowKey = parentKey ? `${parentKey}/${key}` : key;
            const hasChildren = !!item.children && Object.keys(item.children).length > 0;
            const index = rows.length;
            rows.push({ key: rowKey, prefix, depth, parent, item, hasChildren });
            if (hasChildren) walk(item.children!, prefix, rowKey, depth + 1, index);
        });
    };
    walk(data ?? {}, '', '', 0, -1);
    return rows;
}

function ancestorsOf(rows: TOCRow[], index: number): number[] {
    const chain: number[] = [];
    let parent = rows[index]?.parent ?? -1;
    while (parent >= 0 && parent < index && !chain.includes(parent)) {
        chain.unshift(parent);
        parent = rows[parent]?.parent ?? -1;
    }
    return chain;
}

function activeRowKey(rows: TOCRow[], slideNumber: number | null | undefined): string | null {
    if (typeof slideNumber !== 'number') return null;
    let key: string | null = null;
    for (const row of rows) {
        const range = row.item.slide_range;
        if (!range) continue;
        const [start, end] = range;
        if (slideNumber >= start && slideNumber <= end) key = row.key;
    }
    return key;
}

function TOCEntry({
    row,
    first,
    itemIndex,
    active = false,
    onEntrySelect,
    availableSlideNumbers,
    tone = 'default',
}: {
    row: TOCRow;
    first: boolean;
    itemIndex?: number;
    active?: boolean;
    onEntrySelect?: (slideNumber: number) => void;
    availableSlideNumbers?: ReadonlySet<number>;
    tone?: TOCTone;
}) {
    const { goToSlideNumber, hasSlideNumber } = useAudienceSession();
    const { item, prefix, depth, hasChildren } = row;
    const targetSlide = item.slide_range?.[0];
    const reachable =
        typeof targetSlide === 'number' &&
        (onEntrySelect
            ? !availableSlideNumbers || availableSlideNumbers.has(targetSlide)
            : hasSlideNumber(targetSlide));
    const clickable = !hasChildren && reachable;
    const inherit = tone === 'inherit';
    const activeTone = active
        ? inherit
            ? 'font-medium opacity-100 rounded-sm bg-current/10'
            : 'font-medium text-primary hover:text-primary dark:text-primary dark:hover:text-primary'
        : null;
    const textTone = inherit
        ? 'text-current opacity-75'
        : 'text-neutral-700 dark:text-neutral-200';

    const content = (
        <>
            <span className={cn('mr-2', inherit ? 'opacity-60' : 'text-neutral-400 dark:text-neutral-500')}>{prefix}</span>
            {stripInlineMarkdown(item.title)}
        </>
    );

    const itemAttrs =
        typeof itemIndex === 'number'
            ? {
                  [MODULE_ITEM_ATTR]: String(itemIndex),
                  [MODULE_ITEM_PARENT_ATTR]: String(row.parent),
              }
            : {};

    return (
        <li
            {...itemAttrs}
            data-toc-active={active ? '' : undefined}
            className={cn('flex flex-col', !first && (depth === 0 ? 'pt-2' : 'pt-1'))}
        >
            {clickable ? (
                <button
                    type="button"
                    aria-current={active ? 'location' : undefined}
                    onClick={(e) => {
                        if (onEntrySelect) {
                            onEntrySelect(targetSlide!);
                            return;
                        }
                        goToSlideNumber(targetSlide!);
                        emitModuleInteraction(
                            e.currentTarget,
                            'click',
                            { kind: 'link', id: prefix },
                            { targetSlide: targetSlide! },
                        );
                    }}
                    className={cn(
                        'text-sm text-left underline underline-offset-2 cursor-pointer',
                        textTone,
                        inherit ? 'hover:opacity-100' : 'hover:text-neutral-900 dark:hover:text-neutral-100',
                        MODULE_LABEL_WRAP.row,
                        activeTone,
                    )}
                    style={{ paddingLeft: `${depth * 16}px` }}
                >
                    {content}
                </button>
            ) : (
                <span
                    aria-current={active ? 'location' : undefined}
                    className={cn('text-sm', textTone, MODULE_LABEL_WRAP.row, activeTone)}
                    style={{ paddingLeft: `${depth * 16}px` }}
                >
                    {content}
                </span>
            )}
        </li>
    );
}

type TOCTone = 'default' | 'inherit';

interface ModuleTOCProps {
    data: Record<string, TOCItem>;
    slice?: ModuleSlice;
    activeSlideNumber?: number | null;
    onEntrySelect?: (slideNumber: number) => void;
    availableSlideNumbers?: ReadonlySet<number>;
    tone?: TOCTone;
}

export default function ModuleTOC({
    data,
    slice,
    activeSlideNumber,
    onEntrySelect,
    availableSlideNumbers,
    tone,
}: ModuleTOCProps) {
    const rows = useMemo(() => flattenTOC(data), [data]);
    const activeKey = useMemo(
        () => activeRowKey(rows, activeSlideNumber),
        [rows, activeSlideNumber],
    );
    const start = slice ? slice.start : 0;
    const end = slice ? slice.end : rows.length;
    const context = start > 0 ? ancestorsOf(rows, start) : [];
    const shown = rows.slice(start, end);

    if (shown.length === 0) return null;

    return (
        <ul className="flex flex-col">
            {context.map((index, i) => (
                <TOCEntry
                    key={`context-${rows[index].key}`}
                    row={rows[index]}
                    first={i === 0}
                    active={rows[index].key === activeKey}
                    onEntrySelect={onEntrySelect}
                    availableSlideNumbers={availableSlideNumbers}
                    tone={tone}
                />
            ))}
            {shown.map((row, i) => (
                <TOCEntry
                    key={row.key}
                    row={row}
                    first={context.length === 0 && i === 0}
                    itemIndex={start + i}
                    active={row.key === activeKey}
                    onEntrySelect={onEntrySelect}
                    availableSlideNumbers={availableSlideNumbers}
                    tone={tone}
                />
            ))}
        </ul>
    );
}
